import * as React from "react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  cn,
} from "@finster/ui";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import { usePluginModelPicker } from "../featureFlags/usePluginModelPicker";
import {
  findModelPickerEntry,
  getModelPickerEntries,
  type ModelPickerEntry,
  type ModelPickerHost,
  type ModelThinkingEffort,
  type OpenAIReasoningMode,
} from "./catalog";
import { setModelPickerOverride } from "./selectionStore";
import { useModelPickerOverride } from "./useModelPickerOverride";

export interface ModelSettingsDefault {
  readonly model: string;
  readonly effort: ModelThinkingEffort;
  readonly mode?: OpenAIReasoningMode;
}

interface ModelSettingsPickerProps {
  host: ModelPickerHost;
  /** Read when the fail-closed gate opens, after host default flags settle. */
  getDefaultSelection: () => ModelSettingsDefault;
  defaultLabel: string;
  disabled?: boolean;
}

type ModelSettingsView = "overview" | "model" | "effort" | "mode";

function titleCase(value: string): string {
  return value === "xhigh" ? "XHigh" : value.charAt(0).toUpperCase() + value.slice(1);
}

function normalizeDefault(
  entry: ModelPickerEntry | undefined,
  selection: ModelSettingsDefault
): Pick<ModelSettingsDefault, "effort" | "mode"> {
  if (!entry) return selection;
  const effort = entry.allowedEfforts.includes(selection.effort)
    ? selection.effort
    : entry.defaultEffort;
  const allowedModes = entry.allowedModes ?? [];
  const mode =
    allowedModes.length > 0
      ? allowedModes.includes(selection.mode as OpenAIReasoningMode)
        ? selection.mode
        : (entry.defaultMode ?? allowedModes[0])
      : undefined;
  return { effort, ...(mode ? { mode } : {}) };
}

export function ModelSettingsPicker({
  host,
  getDefaultSelection,
  defaultLabel,
  disabled = false,
}: ModelSettingsPickerProps) {
  const gate = usePluginModelPicker();
  const override = useModelPickerOverride(host);
  const [open, setOpen] = React.useState(false);
  const [view, setView] = React.useState<ModelSettingsView>("overview");
  if (!gate.ready || !gate.enabled || !gate.accountScopeKey.trim()) return null;

  const defaultSelection = getDefaultSelection();
  const entries = getModelPickerEntries(host);
  const defaultEntry = findModelPickerEntry(host, defaultSelection.model);
  const normalizedDefault = normalizeDefault(defaultEntry, defaultSelection);
  const activeEntry = override?.entry ?? defaultEntry;
  const activeEffort = override?.effort ?? normalizedDefault.effort;
  const activeMode = override?.mode ?? normalizedDefault.mode;
  const activeModelLabel = activeEntry?.label ?? defaultSelection.model;
  const activeSummary = [
    activeModelLabel,
    titleCase(activeEffort),
    activeMode ? titleCase(activeMode) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const selectModel = (entry: ModelPickerEntry) => (event: Event) => {
    event.preventDefault();
    const effort = entry.allowedEfforts.includes(activeEffort)
      ? activeEffort
      : entry.defaultEffort;
    const allowedModes = entry.allowedModes ?? [];
    const mode =
      allowedModes.length > 0
        ? allowedModes.includes(activeMode as OpenAIReasoningMode)
          ? activeMode
          : (entry.defaultMode ?? allowedModes[0])
        : undefined;
    setModelPickerOverride(host, {
      modelId: entry.id,
      effort,
      ...(mode ? { mode } : {}),
    });
    setView("overview");
  };

  const selectEffort = (effort: ModelThinkingEffort) => (event: Event) => {
    event.preventDefault();
    if (!activeEntry) return;
    setModelPickerOverride(host, {
      modelId: activeEntry.id,
      effort,
      ...(activeMode ? { mode: activeMode } : {}),
    });
    setView("overview");
  };

  const selectMode = (mode: OpenAIReasoningMode) => (event: Event) => {
    event.preventDefault();
    if (!activeEntry) return;
    setModelPickerOverride(host, {
      modelId: activeEntry.id,
      effort: activeEffort,
      mode,
    });
    setView("overview");
  };

  const useDefault = (event: Event) => {
    event.preventDefault();
    setModelPickerOverride(host, null);
    setView("overview");
    setOpen(false);
  };

  const showView = (nextView: ModelSettingsView) => (event: Event) => {
    event.preventDefault();
    setView(nextView);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) setView("overview");
  };

  const viewTitle =
    view === "model"
      ? "Choose model"
      : view === "effort"
        ? "Choose thinking effort"
        : "Choose mode";

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          aria-label={`Model settings: ${activeSummary}`}
          className="h-9 min-w-0 max-w-36 shrink px-3 text-xs"
        >
          <span className="truncate">{activeModelLabel}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="top"
        align="end"
        className="max-h-96 w-72 max-w-[calc(100vw-1rem)] overflow-y-auto"
      >
        {view === "overview" ? (
          <>
            <DropdownMenuLabel className="flex flex-col gap-0.5">
              <span>Model settings</span>
              <span className="truncate text-[11px] font-normal text-muted-foreground">
                {activeSummary}
              </span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={showView("model")}
              className="flex items-center justify-between gap-3"
            >
              <span>Model</span>
              <span className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
                <span className="max-w-40 truncate">{activeModelLabel}</span>
                <ChevronRight className="size-4 shrink-0" aria-hidden />
              </span>
            </DropdownMenuItem>
            {activeEntry ? (
              <DropdownMenuItem
                onSelect={showView("effort")}
                className="flex items-center justify-between gap-3"
              >
                <span>Thinking</span>
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  {titleCase(activeEffort)}
                  <ChevronRight className="size-4 shrink-0" aria-hidden />
                </span>
              </DropdownMenuItem>
            ) : null}
            {activeEntry?.provider === "openai" && activeEntry.allowedModes ? (
              <DropdownMenuItem
                onSelect={showView("mode")}
                className="flex items-center justify-between gap-3"
              >
                <span>Mode</span>
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  {titleCase(activeMode ?? activeEntry.defaultMode ?? "standard")}
                  <ChevronRight className="size-4 shrink-0" aria-hidden />
                </span>
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={useDefault}
              disabled={!override}
              className={cn("flex flex-col items-start", !override && "text-muted-foreground")}
            >
              <span>Reset to {defaultLabel}</span>
              <span className="text-[11px] text-muted-foreground">
                Restores {defaultEntry?.label ?? defaultSelection.model} ·{" "}
                {titleCase(normalizedDefault.effort)}
                {normalizedDefault.mode ? ` · ${titleCase(normalizedDefault.mode)}` : ""}
              </span>
            </DropdownMenuItem>
          </>
        ) : (
          <>
            <DropdownMenuItem
              onSelect={showView("overview")}
              className="flex items-center gap-2 font-medium"
            >
              <ChevronLeft className="size-4 shrink-0" aria-hidden />
              {viewTitle}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {view === "model"
              ? entries.map((entry) => (
                  <DropdownMenuItem
                    key={entry.id}
                    onSelect={selectModel(entry)}
                    className="flex items-center justify-between gap-3"
                  >
                    <span className="truncate">{entry.label}</span>
                    {activeEntry?.id === entry.id ? (
                      <Check className="size-4 shrink-0" aria-hidden />
                    ) : null}
                  </DropdownMenuItem>
                ))
              : null}
            {view === "effort" && activeEntry
              ? activeEntry.allowedEfforts.map((effort) => (
                  <DropdownMenuItem
                    key={effort}
                    onSelect={selectEffort(effort)}
                    className="flex items-center justify-between gap-3"
                  >
                    <span>{titleCase(effort)}</span>
                    {activeEffort === effort ? (
                      <Check className="size-4 shrink-0" aria-hidden />
                    ) : null}
                  </DropdownMenuItem>
                ))
              : null}
            {view === "mode" && activeEntry?.allowedModes
              ? activeEntry.allowedModes.map((mode) => (
                  <DropdownMenuItem
                    key={mode}
                    onSelect={selectMode(mode)}
                    className="flex items-center justify-between gap-3"
                  >
                    <span>{titleCase(mode)}</span>
                    {activeMode === mode ? (
                      <Check className="size-4 shrink-0" aria-hidden />
                    ) : null}
                  </DropdownMenuItem>
                ))
              : null}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
